import * as PIXI from 'pixi.js';
import Polygon from 'polygon';
import roofEdgesModel from '__editor/panelsEditor/models/roofEdges';
import { removeChild } from '__editor/panelsEditor/components/stage/stage';
import { startCoords } from '../../../components/roofsSelector/components/roofsSelectorClassicDesigner/roofsSelectorClassicDesigner';
import { state } from '__common/store';
import {
  bingMapsBackgroundModel,
  googleMapsBackgroundModel,
  whiteBackgroundModel,
} from '__editor/panelsEditor/models/backgrounds';
import { pixel_to_lat_lng } from './utils/backgroundCordinatesConverter';
import { getEditorDims } from '__editor/components/roofsSelector/components/roofsSelectorDrawingManager/roofsSelectorDrawingManager';
import { getPanelsContainer } from '../panels/utils/panelsContainer';
import {
  getRealRoofEdgesBounds,
  getRealRoofEdgesBoundsFromPixiCoords,
  getRoofEdgesPointsInPx,
} from '__editor/panelsEditor/geometry/setbackGeometry';
import { shouldUseVirtualRoofEdges } from '__editor/panelsEditor/panelsEditorHelper';
import roofEdgeDimensionsModel from '__editor/panelsEditor/models/roofEdgeDImensions';
import { getRotatedPolyPixelPoints } from '__editor/panelsEditor/models/fogOfWar';
import moize from 'moize';
import { isRmGridflex10D, isRmGridflex5Rwdi } from '__common/constants/products';

let roofEdgesObj: PIXI.Graphics;

export function getBackgroundContainer() {
  const backgroundContainer: PIXI.Container = new PIXI.Container();
  backgroundContainer.id = 'backgroundContainer';
  return backgroundContainer;
}

export function getBackground(
  x: number,
  y: number,
  rotation: number,
  backgroundType: string,
) {
  switch (backgroundType) {
    case 'google':
      return googleMapsBackgroundModel(x, y, rotation);
    case 'bing':
      return bingMapsBackgroundModel(x, y, rotation);
    case 'white':
      return whiteBackgroundModel(x, y, rotation);
  }
}

export function createRoofEdges(stateConfig?: any) {
  const currentState = stateConfig || state();
  const {
    background: { roofEdges, roofEdgesPixiCords, cords, zoom },
    roofsSelector: { mapType },
    projectConfiguration: { productId, projectVersion },
  } = currentState;

  try {
    const cordsInPx = shouldUseVirtualRoofEdges(mapType, productId, projectVersion) ? roofEdgesPixiCords : getRoofEdgesPointsInPx(roofEdges, cords, zoom, currentState);
    
    if (!cordsInPx || cordsInPx.length === 0) {
      console.warn('No roof edge coordinates available');
      return null;
    }

    const polygon = new Polygon(cordsInPx);
    const cleanedPoints = polygon.clean(true).dedupe(true).points;
    
    if (!cleanedPoints || cleanedPoints.length === 0) {
      console.warn('No valid points after polygon cleaning');
      return null;
    }

    roofEdgesObj = roofEdgesModel(cleanedPoints);
    return roofEdgesObj;
  } catch (error) {
    console.error('Error creating roof edges:', error);
    return null;
  }
}

export function createRoofDimensions(scale: number, stateConfig?: any) {
  const currentState = stateConfig || state();
  const {
    background: { roofEdges },
  } = currentState;

  const RotatedPolyPixelPoints = getRotatedPolyPixelPoints();
  const roofDimensionObj = roofEdgeDimensionsModel(RotatedPolyPixelPoints, roofEdges, scale);
  return roofDimensionObj;
}

export function getRoofEdges(): PIXI.Graphics {
  return roofEdgesObj;
}

export function removeRoofEdge(children: PIXI.Graphics[]) {
  return removeChild(children, 'roofEdges');
}

export const removeBackground = (children: PIXI.DisplayObject[]) => {
  let newChildrens = removeChild(children, 'background');
  newChildrens = removeChild(newChildrens, 'roofEdges');
  return newChildrens;
};

export const getCenterBgCords = (
  editorWidth: number,
  editorHeight: number,
  stateConfig?: any,
): { x: number; y: number; rotationDegrees: number } => {
  const currentState = stateConfig || state();
  const {
    background: { rotationDegrees, bgResized },
  } = currentState;
  const backgroundWidth = bgResized || false ? 1280 * 2 : 1280;
  const backgroundHeight = bgResized || false ? 1280 * 2 : 1280;
  const x = backgroundWidth / 2 - (backgroundWidth - editorWidth) / 2;
  const y = backgroundHeight / 2 - (backgroundHeight - editorHeight) / 2;
  return {
    x,
    y,
    rotationDegrees,
  };
};

// Re-exported from their previous home, so existing importers do not have to
// care that the geometry moved out of this module.
export { getRoofEdgesPointsInPx, getRealRoofEdgesBoundsFromPixiCoords, getRealRoofEdgesBounds };

export function getNewRoofEdgesForClassicDesigner(selectedRoofId?: number) {
  const {
    background: { cords },
    panels: { panels },
    drawingManager: { roofs },
    roofsSelector: { mapType },
    projectConfiguration: { productId, projectVersion },
  } = state();

  if (!panels || panels.length === 0) {
    return startCoords;
  }

  const panelsContainer = getPanelsContainer();
  const roof = roofs[selectedRoofId];
  const roofEdgesPixiCords = roof.roofEdgesPixiCords;

  const { width, height, x, y } = panelsContainer.getLocalBounds();
  const x2 = x + width;
  const y2 = y + height;
  const x3 = x + width;
  const y3 = y;
  const x4 = x;
  const y4 = y + height;
  const bgSize = 1280;

  let topLeft, topRight, bottomRight, bottomLeft;

  if (roofEdgesPixiCords?.length === 4 && (shouldUseVirtualRoofEdges(mapType, productId, projectVersion) ||  isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId))){
    topLeft = pixel_to_lat_lng(
      cords.lat,
      cords.lng,
      20,
      bgSize,
      bgSize,
      roofEdgesPixiCords[0].x,
      roofEdgesPixiCords[0].y,
    );
    topRight = pixel_to_lat_lng(
      cords.lat,
      cords.lng,
      20,
      bgSize,
      bgSize,
      roofEdgesPixiCords[1].x,
      roofEdgesPixiCords[1].y,
    );
    bottomRight = pixel_to_lat_lng(
      cords.lat,
      cords.lng,
      20,
      bgSize,
      bgSize,
      roofEdgesPixiCords[2].x,
      roofEdgesPixiCords[2].y,
    );
    bottomLeft = pixel_to_lat_lng(
      cords.lat,
      cords.lng,
      20,
      bgSize,
      bgSize,
      roofEdgesPixiCords[3].x,
      roofEdgesPixiCords[3].y,
    );
  } else {
    topLeft = pixel_to_lat_lng(
      cords.lat,
      cords.lng,
      20,
      bgSize,
      bgSize,
      x,
      y,
    );
    topRight = pixel_to_lat_lng(
      cords.lat,
      cords.lng,
      20,
      bgSize,
      bgSize,
      x3,
      y3,
    );
    bottomRight = pixel_to_lat_lng(
      cords.lat,
      cords.lng,
      20,
      bgSize,
      bgSize,
      x2,
      y2,
    );
    bottomLeft = pixel_to_lat_lng(
      cords.lat,
      cords.lng,
      20,
      bgSize,
      bgSize,
      x4,
      y4,
    );
  }

  return [
    {
      lat: topLeft.lat,
      lng: topLeft.lng,
    },
    {
      lat: topRight.lat,
      lng: topRight.lng,
    },
    {
      lat: bottomRight.lat,
      lng: bottomRight.lng,
    },
    {
      lat: bottomLeft.lat,
      lng: bottomLeft.lng,
    },
  ];
}

export const getEditorCenter = () => {
  const { width, height } = getEditorDims();

  return {
    x: width / 2,
    y: height / 2,
  };
};

export const getVirtualRoofEdgesPolygon = (roofEdges: pixelPoint[], stateConfig?: any): PolygonInterface => {
  const currentState = stateConfig || state();
  const { settings: { canvasCenter }, background: { bgScale } } = currentState;
  if (!roofEdges) return null;
  return new Polygon(roofEdges.map((edge) => ({
    x: edge.x * bgScale.x + canvasCenter.x,
    y: edge.y * bgScale.y + canvasCenter.y,
  })));
};

export const getRealRoofEdgesBoundsMemoized = moize(getRealRoofEdgesBounds);
